import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { flexiUnitHomeV2QueueQuery$key } from "scmp-app/queries/__generated__/flexiUnitHomeV2QueueQuery.graphql";

import {
  getFlexiUnitArticleLabel,
  getFlexiUnitPodcastLabel,
  getFlexiUnitVideoLabel,
} from "./helpers";
import { Container, StyledContentItemHomeV2FlexiUnit } from "./styles";

type Props = {
  className?: string;
  reference: flexiUnitHomeV2QueueQuery$key;
};

type Settings = {
  custom_text?: string;
};

export const HomeV2FlexiUnit: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment flexiUnitHomeV2QueueQuery on Query
      @argumentDefinitions(focusQueueName: { type: "String!" }) {
        homeV2FlexiUnit: queue(filter: { name: $focusQueueName }) {
          settings
          items(first: 1) {
            edges {
              node {
                ... on Content {
                  entityId
                  urlAlias
                }
                ...helpersGetFlexiUnitArticleLabelArticle
                ...helpersGetFlexiUnitPodcastLabelPodcast
                ...helpersGetFlexiUnitVideoLabelVideo
                ...homeV2FlexiUnitContentItemContent
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const article = first(data?.homeV2FlexiUnit?.items?.edges)?.node;
  const blockSection = findFirstNonEmptyString(
    (data?.homeV2FlexiUnit?.settings as Settings)?.custom_text ?? "",
    getFlexiUnitPodcastLabel(article),
    getFlexiUnitVideoLabel(article),
    getFlexiUnitArticleLabel(article),
    "Focus",
  );

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: blockSection,
        list_of_articles: article?.entityId,
        page_type: currentPageType,
        widget_name: "flexi_unit",
      },
      subcategory: "widget",
    }),
    [article?.entityId, blockSection, currentPageType],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (!article) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.FlexiUnitFocus,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <StyledContentItemHomeV2FlexiUnit
          label={blockSection}
          onClick={entityId => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                block_section: blockSection,
                destination_url: getAbsoluteUrl(article?.urlAlias),
                page_type: currentPageType,
                widget_name: "flexi_unit",
              },
              subcategory: "widget",
            });
          }}
          reference={article}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2FlexiUnit.displayName = "HomeV2FlexiUnit";
